import {
  BookOpen,
  Building2,
  CalendarRange,
  Clock,
  Globe2,
  GraduationCap,
  LayoutDashboard,
  Layers,
  School,
  Settings,
  ShieldCheck,
  UserCog,
  UserRound,
  UsersRound,
  type LucideIcon,
} from 'lucide-react'
import type { Permission } from '@/features/auth/types'
import type { SchoolModuleKey } from '@/features/school/modules'
import { appModules, type AdminNavGroup } from '@/routes/moduleRegistry'

export interface NavItem {
  label: string
  to: string
  icon?: LucideIcon
  permission?: Permission
  /** Only active on exact match (dashboards). */
  end?: boolean
  children?: NavItem[]
  /** Extra search terms for the command palette. */
  keywords?: string
  /** Set from the contributing module: hidden when the school has that module off. */
  module?: SchoolModuleKey
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * Admin console information architecture — mirrors the reference sidebar grouping
 * (Dashboard · People · Academic · … · Settings), filtered by permission at render time.
 */
const coreAdminNavigation: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard, end: true, permission: 'dashboard.view' },
    ],
  },
  {
    label: 'People',
    items: [
      {
        label: 'Students',
        to: '/admin/students',
        icon: UsersRound,
        permission: 'students.view',
        children: [
          { label: 'All students', to: '/admin/students', permission: 'students.view', end: true },
          {
            label: 'Admit a student',
            to: '/admin/students/new',
            permission: 'students.manage',
            keywords: 'new enroll admission add',
          },
          {
            label: 'Next year',
            to: '/admin/students/next-year',
            permission: 'students.view',
            keywords: 'promotion re-enrolment reenrollment repeat graduate placement passage',
          },
        ],
      },
      {
        label: 'Staff',
        to: '/admin/staff',
        icon: UserRound,
        permission: 'staff.view',
        children: [
          {
            label: 'Staff directory',
            to: '/admin/staff',
            permission: 'staff.view',
            end: true,
            keywords: 'teachers employees',
          },
          {
            label: 'Hire staff',
            to: '/admin/staff/new',
            permission: 'staff.manage',
            keywords: 'new add employee',
          },
          { label: 'Departments', to: '/admin/staff/departments', permission: 'staff.view' },
          {
            label: 'Designations',
            to: '/admin/staff/designations',
            permission: 'staff.view',
            keywords: 'job titles positions',
          },
        ],
      },
    ],
  },
  {
    label: 'Academics',
    items: [
      {
        label: 'Academic years',
        to: '/admin/academic/years',
        icon: CalendarRange,
        permission: 'academic.view',
        keywords: 'session term',
      },
      {
        label: 'Classes & sections',
        to: '/admin/academic/classes',
        icon: Layers,
        permission: 'academic.view',
        keywords: 'grades curriculum',
      },
      {
        label: 'Subjects',
        to: '/admin/academic/subjects',
        icon: BookOpen,
        permission: 'academic.view',
        keywords: 'courses',
      },
      { label: 'Shifts', to: '/admin/academic/shifts', icon: Clock, permission: 'academic.view' },
    ],
  },
  {
    label: 'Administration',
    items: [
      {
        label: 'Settings',
        to: '/admin/settings',
        icon: Settings,
        permission: 'school.manage',
        children: [
          {
            label: 'School profile',
            to: '/admin/settings/school',
            icon: School,
            permission: 'school.manage',
            keywords: 'logo opening hours locale',
          },
          {
            label: 'Homepage',
            to: '/admin/settings/homepage',
            icon: Globe2,
            permission: 'school.manage',
            keywords: 'public website site page accueil',
          },
          {
            label: 'Users & roles',
            to: '/admin/settings/users',
            icon: ShieldCheck,
            permission: 'users.manage',
            keywords: 'accounts permissions',
          },
        ],
      },
    ],
  },
]

const coreStaffNavigation: NavGroup[] = [
  {
    label: 'My workspace',
    items: [
      { label: 'Dashboard', to: '/staff', icon: LayoutDashboard, end: true },
      { label: 'My classes', to: '/staff/classes', icon: GraduationCap, permission: 'academic.view' },
      { label: 'Profile', to: '/staff/profile', icon: UserCog },
    ],
  },
]

const coreFamilyNavigation: NavGroup[] = [
  {
    label: 'Family portal',
    items: [
      { label: 'Overview', to: '/portal', icon: LayoutDashboard, end: true },
      { label: 'School', to: '/portal/school', icon: Building2 },
      { label: 'Account', to: '/portal/account', icon: UserCog },
    ],
  },
]

/** Removes items (and empty groups) the user may not see. */
export function filterNavigation(
  groups: NavGroup[],
  can: (p: Permission) => boolean,
  hasModule: (m: SchoolModuleKey) => boolean = () => true,
): NavGroup[] {
  const visible = (item: NavItem): NavItem | null => {
    if (item.permission && !can(item.permission)) return null
    if (item.module && !hasModule(item.module)) return null
    if (!item.children) return item
    const children = item.children.map(visible).filter((c): c is NavItem => c !== null)
    return children.length ? { ...item, children } : null
  }
  return groups
    .map((g) => ({ ...g, items: g.items.map(visible).filter((i): i is NavItem => i !== null) }))
    .filter((g) => g.items.length > 0)
}

export function flattenNavigation(groups: NavGroup[]): (NavItem & { group: string })[] {
  const out: (NavItem & { group: string })[] = []
  for (const g of groups) {
    for (const item of g.items) {
      if (item.children) {
        for (const child of item.children)
          out.push({ ...child, icon: child.icon ?? item.icon, group: `${g.label} · ${item.label}` })
      } else out.push({ ...item, group: g.label })
    }
  }
  return out
}

/** Fixed order of admin sidebar groups; modules contribute items into these by label. */
const ADMIN_GROUP_ORDER: AdminNavGroup[] = [
  'Overview',
  'People',
  'Academics',
  'Operations',
  'Finance',
  'Communication',
  'Resources',
  'Administration',
]

function withModule(item: NavItem, module: SchoolModuleKey | undefined): NavItem {
  return module ? { ...item, module } : item
}

function mergeAdmin(): NavGroup[] {
  const byLabel = new Map<string, { order: number; item: NavItem }[]>()
  for (const group of coreAdminNavigation) {
    byLabel.set(
      group.label,
      group.items.map((item, i) => ({ order: i * 10, item })),
    )
  }
  for (const m of appModules) {
    for (const c of m.nav?.admin ?? []) {
      const list = byLabel.get(c.group) ?? []
      list.push({ order: c.order, item: withModule(c.item, m.schoolModule) })
      byLabel.set(c.group, list)
    }
  }
  return ADMIN_GROUP_ORDER.filter((label) => byLabel.has(label)).map((label) => ({
    label,
    items: (byLabel.get(label) ?? []).sort((a, b) => a.order - b.order).map((e) => e.item),
  }))
}

function mergeFlat(core: NavGroup[], key: 'staff' | 'family'): NavGroup[] {
  const first = core[0]
  if (!first) return core
  const entries = first.items.map((item, i) => ({ order: i * 10, item }))
  for (const m of appModules)
    for (const c of m.nav?.[key] ?? [])
      entries.push({ order: c.order, item: withModule(c.item, m.schoolModule) })
  return [
    { label: first.label, items: entries.sort((a, b) => a.order - b.order).map((e) => e.item) },
    ...core.slice(1),
  ]
}

export const adminNavigation: NavGroup[] = mergeAdmin()
export const staffNavigation: NavGroup[] = mergeFlat(coreStaffNavigation, 'staff')
export const familyNavigation: NavGroup[] = mergeFlat(coreFamilyNavigation, 'family')
